/**
 * FlywheelConfigModal Component
 * Configuration panel for enabling and configuring the autonomous follow-up flywheel
 *
 * Uses React Hook Form with Zod validation for best practice form handling
 */

import React, { useState, useMemo } from 'react';
import { Modal, Button, Form, Select, useToast, Typography, Alert, AlertDescription } from '@crisellevaldez/speculo-ui';
import { Organization } from '../../../types/organization.types';
import { TIMEZONE_OPTIONS } from '../../../constants/timezones';
import { PlusCircle } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  flywheelConfigSchema,
  type FlywheelConfigFormData,
} from '../../../schemas/flywheelConfig.schema';
import {
  useFlywheelFlow,
  useSaveFlywheelConfig,
  getDefaultFlywheelConfig,
  DAYS_OF_WEEK,
} from '../../../hooks/organizations/useFlywheelConfig';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import {
  buildTransferLineOptions,
  formatTransferNumbers,
} from '../../../utils/flows/formatTransferNumbers';
import { TeamRosterModal } from '../header/TeamRosterModal';

// ============================================================================
// Types
// ============================================================================

interface FlywheelConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`,
}));

// ============================================================================
// Component
// ============================================================================

export const FlywheelConfigModal: React.FC<FlywheelConfigModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  const { showToast } = useToast();

  // Fetch existing flow data — only when the modal is actually open so we
  // don't spam GET_ORGANIZATION_FLOWS for every org click in ClientAdmin.
  const { data: flowData, isLoading } = useFlywheelFlow(selectedOrg?.id, { enabled: isOpen });

  // Team members - use selectedOrg.id to get team members for the selected organization
  const { users, loading: loadingUsers } = useOrganizationUsers({
    organizationId: selectedOrg?.id,
    enabled: isOpen,
  });
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Mutations
  const { mutateAsync: saveConfig, isPending: isSaving } = useSaveFlywheelConfig();

  // Compute default values from selectedOrg and flowData
  const defaultValues = useMemo((): FlywheelConfigFormData => {
    const defaults = getDefaultFlywheelConfig(selectedOrg?.timezone);

    if (!flowData) {
      return {
        flywheelEnabled: selectedOrg?.settings?.flywheel_enabled ?? false,
        transferLines: [],
        startHour: String(defaults.startHour),
        endHour: String(defaults.endHour),
        selectedDays: defaults.selectedDays,
        timezone: '', // Start empty to force user selection
        countryCode: 'US',
      };
    }

    // Load from existing flow data - get phone numbers as array
    const transferLines = flowData.transfer_numbers ? Object.values(flowData.transfer_numbers) : [];

    return {
      flywheelEnabled: selectedOrg?.settings?.flywheel_enabled ?? false,
      transferLines,
      startHour: String(flowData.allowed_hours?.start_hour ?? defaults.startHour),
      endHour: String(flowData.allowed_hours?.end_hour ?? defaults.endHour),
      selectedDays:
        flowData.allowed_days?.length > 0 ? flowData.allowed_days : defaults.selectedDays,
      timezone: flowData.timezone || '', // Empty if no existing config
      countryCode: 'US',
    };
  }, [selectedOrg, flowData]);

  // React Hook Form setup with Zod resolver
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FlywheelConfigFormData>({
    resolver: zodResolver(flywheelConfigSchema),
    // `values` reactively syncs the form to the async-loaded flow config, so
    // the fields populate once the GraphQL fetch resolves (no useEffect/reset
    // dance). keepDirtyValues preserves any edits made while it loads.
    values: defaultValues,
    resetOptions: { keepDirtyValues: true },
    mode: 'onChange',
  });

  // Watch the flywheelEnabled field for conditional rendering
  const flywheelEnabled = watch('flywheelEnabled');
  const selectedDays = watch('selectedDays');

  // Build transfer line options from users and existing flow data
  const transferLineOptions = useMemo(
    () => (loadingUsers ? [] : buildTransferLineOptions(users, flowData?.transfer_numbers)),
    [users, flowData?.transfer_numbers, loadingUsers]
  );

  // Toggle day selection
  const toggleDay = (day: string) => {
    const currentDays = selectedDays || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter((d) => d !== day)
      : [...currentDays, day];
    setValue('selectedDays', newDays, { shouldValidate: true });
  };

  // Form submission handler
  const onSubmit = async (data: FlywheelConfigFormData) => {
    if (!selectedOrg) return;

    // Build transfer_numbers object from selected lines
    const selectedUsersForTransfer = transferLineOptions.filter((u) =>
      data.transferLines.includes(u.value)
    );
    const transferNumbers = formatTransferNumbers(selectedUsersForTransfer);

    try {
      await saveConfig({
        organizationId: selectedOrg.id,
        organizationName: selectedOrg.org_name,
        flywheelEnabled: data.flywheelEnabled,
        transferNumbers,
        startHour: parseInt(data.startHour),
        endHour: parseInt(data.endHour),
        selectedDays: data.selectedDays,
        timezone: data.timezone,
        countryCode: data.countryCode,
        excludeHolidays: true, // Always exclude holidays
      });

      showToast({
        message: data.flywheelEnabled
          ? 'Flywheel enabled and configured successfully'
          : 'Flywheel disabled successfully',
        variant: 'success',
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error saving flywheel config:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to save flywheel configuration',
        variant: 'error',
      });
    }
  };

  if (!selectedOrg) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <Typography variant="h3">Flywheel Configuration</Typography>
      </Modal.Header>
      <Modal.Body className="py-8">
        <form id="flywheel-config-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-6">
            <Alert variant="info">
              <AlertDescription>
                The Follow-up Flywheel is Remi&apos;s automated follow-up system. Remi analyzes each
                contact&apos;s activity (calls, website visits, emails) and schedules follow-ups at optimal
                times. This continues automatically until you hand off or remove contacts from the
                flywheel.
              </AlertDescription>
            </Alert>

            {/* Enable Toggle. Enabling Flywheel is temporarily blocked at the
                UI level — admins can still disable orgs that are currently on,
                but cannot turn it on for orgs that are currently off. */}
            {(() => {
              const isCurrentlyEnabled = selectedOrg?.settings?.flywheel_enabled === true;
              const enableBlocked = !isCurrentlyEnabled;
              return (
                <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-foreground">Enable Follow-Up Flywheel</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {flywheelEnabled
                          ? 'Flywheel is active for this organization'
                          : enableBlocked
                            ? 'Enabling Flywheel is temporarily disabled'
                            : 'Flywheel is disabled for this organization'}
                      </p>
                    </div>
                    <Controller
                      name="flywheelEnabled"
                      control={control}
                      render={({ field }) => (
                        <label
                          className={`relative inline-flex items-center ${enableBlocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                          title={enableBlocked ? 'Enabling Flywheel is temporarily disabled' : undefined}
                        >
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            disabled={isLoading || isSaving || enableBlocked}
                            className="sr-only peer"
                          />
                          <div className={`w-11 h-6 bg-gray-200 dark:bg-white/10 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500 ${enableBlocked ? 'opacity-50' : ''}`}></div>
                        </label>
                      )}
                    />
                  </div>
                  {enableBlocked && (
                    <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">
                      Enabling Flywheel is temporarily disabled. Reach out to the Speculo team to enable it for this organization.
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Configuration Form - Only visible when enabled */}
            {flywheelEnabled && (
              <>
                {/* Transfer Lines */}
                <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
                  <h4 className="text-sm font-bold mb-3 text-foreground">
                    Transfer Line(s) <span className="text-red-500">*</span>
                  </h4>
                  <hr className="border-border" />
                  <div className="mt-5">
                    <Form.Control required>
                      <div className="flex gap-2">
                        <Controller
                          name="transferLines"
                          control={control}
                          render={({ field }) => (
                            <Select
                              isLoading={loadingUsers}
                              options={transferLineOptions}
                              className="min-h-9"
                              value={field.value}
                              rows={6}
                              onChange={(value) => {
                                if (Array.isArray(value)) {
                                  field.onChange(value);
                                } else if (typeof value === 'string') {
                                  field.onChange([value]);
                                }
                              }}
                              placeholder="Select team members"
                              multiple
                              searchable
                            />
                          )}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="w-52 items-center gap-2 h-9"
                          onClick={() => setShowInviteModal(true)}
                        >
                          <PlusCircle className="w-5 h-5" />
                          Add New
                        </Button>
                      </div>
                    </Form.Control>
                    {errors.transferLines && (
                      <p className="text-sm text-red-500 mt-2">{errors.transferLines.message}</p>
                    )}
                  </div>
                </div>

                {/* Business Hours */}
                <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
                  <h4 className="text-sm font-bold mb-3 text-foreground">Business Hours</h4>
                  <hr className="border-border" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
                    <Form.Control required error={errors.startHour?.message}>
                      <Form.Label>Start Time</Form.Label>
                      <Controller
                        name="startHour"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onChange={(val) =>
                              field.onChange(typeof val === 'string' ? val : String(val))
                            }
                            options={HOUR_OPTIONS}
                            placeholder="Select start time"
                          />
                        )}
                      />
                    </Form.Control>
                    <Form.Control required error={errors.endHour?.message}>
                      <Form.Label>End Time</Form.Label>
                      <Controller
                        name="endHour"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onChange={(val) =>
                              field.onChange(typeof val === 'string' ? val : String(val))
                            }
                            options={HOUR_OPTIONS}
                            placeholder="Select end time"
                          />
                        )}
                      />
                    </Form.Control>
                    <Form.Control required>
                      <Form.Label>Timezone</Form.Label>
                      <Controller
                        name="timezone"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onChange={(val) => {
                              const newTimezone = typeof val === 'string' ? val : String(val);
                              field.onChange(newTimezone);
                              // Auto-set country based on timezone
                              if (
                                newTimezone.includes('Canada') ||
                                newTimezone.startsWith('Canada/')
                              ) {
                                setValue('countryCode', 'CA');
                              } else {
                                setValue('countryCode', 'US');
                              }
                            }}
                            options={TIMEZONE_OPTIONS}
                            placeholder="Select timezone"
                            searchable
                          />
                        )}
                      />
                      {errors.timezone && (
                        <p className="text-sm text-red-500 mt-1">{errors.timezone.message}</p>
                      )}
                    </Form.Control>
                  </div>
                </div>

                {/* Allowed Days */}
                <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
                  <h4 className="text-sm font-bold mb-3 text-foreground">
                    Allowed Days <span className="text-red-500">*</span>
                  </h4>
                  <hr className="border-border" />
                  <div className="flex flex-wrap gap-2 mt-5">
                    {DAYS_OF_WEEK.map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDay(day)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          selectedDays?.includes(day)
                            ? 'bg-black text-white dark:bg-amber-400 dark:text-amber-950'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-300 dark:hover:bg-white/20'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                  {errors.selectedDays && (
                    <p className="text-sm text-red-500 mt-2">{errors.selectedDays.message}</p>
                  )}
                </div>
              </>
            )}

          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex gap-2 w-full md:w-auto ml-auto justify-end">
          {/* <Button type="button" variant="outline" onClick={handleVerify} isLoading={isVerifying}>
            Verify
          </Button> */}
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="flywheel-config-form"
            isLoading={isSaving}
            disabled={isLoading}
          >
            Save Configuration
          </Button>
        </div>
      </Modal.Footer>

      <TeamRosterModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />
    </Modal>
  );
};

export default FlywheelConfigModal;
